import loadable from '@loadable/component';
import { OverlaySpinner } from '@ankr.com/ui';
import { Outlet, Route } from 'react-router';

import { Page } from 'modules/layout/components/Page';
import { createRouteConfig } from 'modules/router/utils/createRouteConfig';
import { useQueryParams } from 'modules/common/hooks/useQueryParams';
import { NoSSRGuard } from 'modules/common/components/NoSSRGuard';

import { TeamInvitationQueryParams } from './types';
import { AuthUserSettingsGuard } from './components/AuthUserSettingsGuard';
import { TeamInvitationGuard } from './components/TeamInvitationGuard';
import { EmailConfirmationGuard } from './components/EmailConfirmationGuard';
import { TelegramConfirmationGuard } from './components/TelegramConfirmationGuard';
import { SlackConfirmationGuard } from './components/SlackConfirmationGuard';

export const PATH_SETTINGS = '/settings/';

const PATH_NOTIFICATIONS = `${PATH_SETTINGS}notifications/`;
const PATH_TEAM_GENERAL = `${PATH_SETTINGS}team-general/`;
const PATH_TEAMMATES = `${PATH_SETTINGS}teammates/`;
const PATH_TEAM_NOTIFICATIONS = `${PATH_SETTINGS}team-notifications/`;

export const PATH_CONFIRMATION = `${PATH_SETTINGS}confirmation/`;

const PATH_TEAM_INVITATION = `${PATH_SETTINGS}invitation/`;
const PATH_TELEGRAM_CONFIRMATION = `${PATH_SETTINGS}telegram/`;
const PATH_SLACK_CONFIRMATION = `${PATH_SETTINGS}slack/`;
const PATH_EMAIL_CONFIRMATION = `${PATH_SETTINGS}email/`;

export const UserSettingsRoutesConfig = createRouteConfig(
  {
    confirmation: {
      path: PATH_CONFIRMATION,
      generatePath: () => PATH_CONFIRMATION,
    },
    notifications: {
      path: PATH_NOTIFICATIONS,
      generatePath: () => PATH_NOTIFICATIONS,
    },
    teamGeneral: {
      path: PATH_TEAM_GENERAL,
      generatePath: () => PATH_TEAM_GENERAL,
    },
    teammates: {
      path: PATH_TEAMMATES,
      generatePath: () => PATH_TEAMMATES,
    },
    teamNotifications: {
      path: PATH_TEAM_NOTIFICATIONS,
      generatePath: () => PATH_TEAM_NOTIFICATIONS,
    },
    telegramConfirmation: {
      path: PATH_TELEGRAM_CONFIRMATION,
      generatePath: () => PATH_TELEGRAM_CONFIRMATION,
    },
    slackConfirmation: {
      path: PATH_SLACK_CONFIRMATION,
      generatePath: () => PATH_SLACK_CONFIRMATION,
    },
    emailConfirmation: {
      path: PATH_EMAIL_CONFIRMATION,
      generatePath: () => PATH_EMAIL_CONFIRMATION,
    },
    settings: {
      path: PATH_SETTINGS,
      generatePath: () => {
        return PATH_SETTINGS;
      },
    },
    teamInvitation: {
      path: PATH_TEAM_INVITATION,
      generatePath: (params: TeamInvitationQueryParams) =>
        `${PATH_TEAM_INVITATION}?${new URLSearchParams({
          ...params,
        }).toString()}`,
      useQuery: () => {
        const search = useQueryParams();
        const entries = search.entries();

        return Object.fromEntries(
          entries,
        ) as unknown as TeamInvitationQueryParams;
      },
    },
  },
  PATH_SETTINGS,
);

const LoadableConfirmationContainer = loadable(
  async () =>
    import('./screens/Confirmation').then(module => module.Confirmation),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadablePersonalNotificationsContainer = loadable(
  async () =>
    import('./screens/PersonalNotifications').then(
      module => module.PersonalNotifications,
    ),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableTeamGeneralContainer = loadable(
  async () =>
    import('./screens/TeamGeneral').then(module => module.TeamGeneral),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableTeamNotificationsContainer = loadable(
  async () =>
    import('./screens/TeamNotifications').then(
      module => module.TeamNotifications,
    ),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableTeammatesContainer = loadable(
  async () => import('./screens/Teammates').then(module => module.Teammates),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableTelegramConfirmationContainer = loadable(
  async () =>
    import('./screens/TelegramConfirmation').then(
      module => module.TelegramConfirmation,
    ),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableSlackConfirmationContainer = loadable(
  async () =>
    import('./screens/SlackConfirmation').then(
      module => module.SlackConfirmation,
    ),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableEmailConfirmationContainer = loadable(
  async () =>
    import('./screens/EmailForNotificationsConfirmation').then(
      module => module.EmailForNotificationsConfirmation,
    ),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableSettingsContainer = loadable(
  async () => import('./screens/Settings').then(module => module.Settings),
  {
    fallback: <OverlaySpinner />,
  },
);

const LoadableTeamInvitationContainer = loadable(
  async () =>
    import('./screens/TeamInvitation').then(module => module.TeamInvitation),
  {
    fallback: <OverlaySpinner />,
  },
);

export function getUserSettingsRoutes() {
  return (
    <>
      <Route
        element={
          <AuthUserSettingsGuard>
            <Outlet />
          </AuthUserSettingsGuard>
        }
      >
        <Route
          path={UserSettingsRoutesConfig.confirmation.path}
          element={<LoadableConfirmationContainer />}
        />
        <Route
          path={UserSettingsRoutesConfig.notifications.path}
          element={
            <Page>
              <LoadablePersonalNotificationsContainer />
            </Page>
          }
        />
        <Route
          path={UserSettingsRoutesConfig.teamGeneral.path}
          element={
            <Page>
              <LoadableTeamGeneralContainer />
            </Page>
          }
        />
        <Route
          path={UserSettingsRoutesConfig.teammates.path}
          element={
            <Page>
              <LoadableTeammatesContainer />
            </Page>
          }
        />
        <Route
          path={UserSettingsRoutesConfig.teamNotifications.path}
          element={
            <Page>
              <LoadableTeamNotificationsContainer />
            </Page>
          }
        />
        <Route
          path={UserSettingsRoutesConfig.settings.path}
          element={
            <Page>
              <LoadableSettingsContainer />
            </Page>
          }
        />
      </Route>
      <Route
        path={UserSettingsRoutesConfig.telegramConfirmation.path}
        element={
          <TelegramConfirmationGuard>
            <LoadableTelegramConfirmationContainer />
          </TelegramConfirmationGuard>
        }
      />
      <Route
        path={UserSettingsRoutesConfig.slackConfirmation.path}
        element={
          <SlackConfirmationGuard>
            <LoadableSlackConfirmationContainer />
          </SlackConfirmationGuard>
        }
      />
      <Route
        path={UserSettingsRoutesConfig.emailConfirmation.path}
        element={
          <EmailConfirmationGuard>
            <LoadableEmailConfirmationContainer />
          </EmailConfirmationGuard>
        }
      />
      <Route
        path={UserSettingsRoutesConfig.teamInvitation.path}
        element={
          <NoSSRGuard>
            <TeamInvitationGuard>
              <LoadableTeamInvitationContainer />
            </TeamInvitationGuard>
          </NoSSRGuard>
        }
      />
    </>
  );
}
